import { useParams, Link } from 'react-router-dom';
import PageLayout from '@/components/feature/PageLayout';
import ArticleCard from '@/components/feature/ArticleCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles, communityPosts } from '@/mocks/data';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  const isCommunityPost = communityPosts.find((p) => p.slug === slug);

  if (!article && !isCommunityPost) {
    return (
      <PageLayout>
        <div className="max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-heading font-semibold text-foreground-900 mb-3">文章未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
        </div>
      </PageLayout>
    );
  }

  if (isCommunityPost && !article) {
    return (
      <PageLayout>
        <SEOMeta
          title={`${isCommunityPost.title} – 挠脚心vk社区`}
          description={isCommunityPost.title}
          keywords="挠脚心vk社区,挠脚心vk讨论,社区帖子"
          canonicalUrl={`https://naojiaoxinvk.com/article/${slug}`}
          ogType="article"
        />
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '社区讨论', href: '/category/shequ' }, { label: isCommunityPost.title }]} />
        <article className="max-w-[800px] mx-auto px-4 md:px-6 py-8 md:py-10">
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-4">{isCommunityPost.title}</h1>
          <div className="flex items-center gap-4 text-sm text-foreground-500 mb-6 pb-6 border-b border-background-200/70">
            <span><i className="ri-user-line mr-1"></i>{isCommunityPost.author}</span>
            <span><i className="ri-calendar-line mr-1"></i>{isCommunityPost.date}</span>
            <span><i className="ri-chat-1-line mr-1"></i>{isCommunityPost.replies} 回复</span>
            <span><i className="ri-heart-line mr-1"></i>{isCommunityPost.likes} 赞</span>
          </div>
          <div className="text-foreground-700 text-sm leading-relaxed">
            <p>这是一个来自挠脚心vk社区的讨论帖。在这里，用户们围绕着"{isCommunityPost.title}"这一话题展开了热烈的讨论。</p>
            <p className="mt-4">感谢您对挠脚心vk社区的关注！更多精彩讨论，请浏览社区讨论区。</p>
          </div>
        </article>
        <section className="max-w-[800px] mx-auto px-4 md:px-6 pb-10">
          <Link to="/category/shequ" className="inline-flex items-center px-5 py-2.5 bg-primary-500 text-background-50 rounded-lg text-sm font-medium hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer">
            <i className="ri-arrow-left-line mr-1.5"></i>返回社区讨论
          </Link>
        </section>
      </PageLayout>
    );
  }

  // Full article view
  const relatedArticles = articles.filter((a) => a.slug !== slug && a.category === article!.category).slice(0, 3);

  const categoryMap: Record<string, string> = {
    shipin: '视频资源',
    shequ: '社区讨论',
    wenzhang: '文章资讯',
    huati: '话题标签',
    ziyuan: '资源合集',
  };

  return (
    <PageLayout>
      <SEOMeta
        title={`${article!.title} – 挠脚心vk`}
        description={article!.excerpt}
        keywords={`挠脚心vk,${article!.topics.map((t: string) => t).join(',')}`}
        canonicalUrl={`https://naojiaoxinvk.com/article/${slug}`}
        ogType="article"
      />
      <SchemaOrg data={{
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: article!.title,
        description: article!.excerpt,
        datePublished: article!.date,
        url: `https://naojiaoxinvk.com/article/${slug}`,
        inLanguage: 'zh-CN',
        author: { '@type': 'Organization', name: '挠脚心vk' },
        publisher: { '@type': 'Organization', name: '挠脚心vk', url: 'https://naojiaoxinvk.com/' },
      }} />
      <SchemaOrg data={{
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://naojiaoxinvk.com/' },
          { '@type': 'ListItem', position: 2, name: categoryMap[article!.category] || article!.category, item: `https://naojiaoxinvk.com/category/${article!.category}` },
          { '@type': 'ListItem', position: 3, name: article!.title, item: `https://naojiaoxinvk.com/article/${slug}` },
        ],
      }} />

      <Breadcrumb items={[
        { label: '首页', href: '/' },
        { label: categoryMap[article!.category] || article!.category, href: `/category/${article!.category}` },
        { label: article!.title },
      ]} />

      <article className="max-w-[800px] mx-auto px-4 md:px-6 py-8 md:py-10">
        <div className="mb-6">
          <span className="inline-block bg-primary-100 text-primary-700 text-xs px-2.5 py-1 rounded-md mb-3 whitespace-nowrap">
            {categoryMap[article!.category] || article!.category}
          </span>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-foreground-900 mb-3">{article!.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
            <span className="flex items-center gap-1"><i className="ri-calendar-line"></i>{article!.date}</span>
            <span className="flex items-center gap-1"><i className="ri-time-line"></i>阅读 {article!.readTime}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{article!.viewCount.toLocaleString()} 阅读</span>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {article!.topics.map((t: string) => (
              <Link key={t} to={`/category/huati`} className="text-xs px-2 py-1 bg-secondary-100 text-secondary-700 rounded-full hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap">#{t.replace(/-/g, '')}</Link>
            ))}
          </div>
        </div>

        <div className="w-full h-64 md:h-80 rounded-lg overflow-hidden mb-8">
          <img src={article!.coverImage} alt={article!.title} title={`${article!.title} - 挠脚心vk`} className="w-full h-full object-cover" />
        </div>

        <div className="article-content text-foreground-700 leading-relaxed" dangerouslySetInnerHTML={{ __html: article!.content }} />

        <div className="mt-10 pt-6 border-t border-background-200/70 flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <span className="text-sm text-foreground-500">分享：</span>
            <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"><i className="ri-wechat-line"></i></a>
            <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"><i className="ri-weibo-line"></i></a>
            <a href="#" className="w-8 h-8 flex items-center justify-center rounded-full bg-background-100 text-foreground-600 hover:bg-primary-100 hover:text-primary-600 transition-colors cursor-pointer"><i className="ri-link-m"></i></a>
          </div>
          <Link to={`/category/${article!.category}`} className="inline-flex items-center text-sm text-primary-500 hover:text-primary-600 transition-colors cursor-pointer">
            <i className="ri-arrow-left-line mr-1"></i>返回{categoryMap[article!.category] || article!.category}
          </Link>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="bg-background-100 py-10 md:py-14">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <h2 className="text-xl font-heading font-semibold text-foreground-900 mb-6">相关推荐</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {relatedArticles.map((a) => (
                <ArticleCard key={a.slug} {...a} />
              ))}
            </div>
          </div>
        </section>
      )}
    </PageLayout>
  );
}